import { useMemo, memo, useEffect } from 'react';

import { Card } from 'features/deals/dealCards/CardWrapper';
import DiligenceController from 'features/deals/dealCards/Diligence/DiligenceController';
import { LoansCardBody } from 'features/deals/dealCards/LoansCard';
import { StratCardBody } from 'features/deals/dealCards/StratsCard';
import BiddingAndPricingController from 'features/pages/marketplace/BiddingAndPricing/BiddingAndPricingController';
import { useDealRoomContext } from 'features/pages/marketplace/DealRoomContext';
import { DateTime } from 'luxon';
import { Tooltip } from 'react-tooltip';

import BidCardBody from './BidCardBody';
import CarveDetailsCardBody from './CarveDetailsCardBody';
import CounterpartyCardBody from './CounterpartyCardBody';
import CounterpartyInsightsBody from './CounterpartyInsightsBody';
import DealDocumentsCardBody from './DealDocumentsCardBody';
import SettlementCardBody from './Settlement';
import { findLastIndex } from './utils';
import WireConfirmationCardBody from './WireConfirmationCardBody';
import { GetDealGeneralInfo_deal_listing_UserCompanyListing_seller } from '../../dealCards/gql/dealDetailsQueries';

import { GetTimeline } from '../__generated__/fragments';
import { DealStagesDeal_state } from '../fragments';
import { useIsTimelineCardShown } from '../useIsTimelineCardShown';

type GetTimeline_deal_cards = NonNullable<
  NonNullable<NonNullable<GetTimeline>['deal']>['cards']
>[number];
function renderCardBody(
  card: GetTimeline_deal_cards,
  dealId: string,
  role: string,
  seller: GetDealGeneralInfo_deal_listing_UserCompanyListing_seller,
  dealState: DealStagesDeal_state,
  isTimelineCardExpanded: boolean,
  cardIndex?: number,
) {
  switch (card.__typename) {
    case 'CounterpartyCard':
      return <CounterpartyCardBody dealId={dealId} {...card} role={role} />;
    case 'CounterpartyInsightsCard':
      return <CounterpartyInsightsBody dealId={dealId} />;
    case 'DealDocumentsCard':
      return <DealDocumentsCardBody dealId={dealId} {...card} />;
    case 'StratificationsCard':
      return <StratCardBody dealId={dealId} {...card} />;
    case 'LoansCard':
      return <LoansCardBody dealId={dealId} role={role} {...card} />;
    case 'CarveDetailsCard':
      return <CarveDetailsCardBody dealId={dealId} cardIndex={cardIndex} {...card} />;
    case 'DiligenceCard':
      return <DiligenceController dealId={dealId} isTabActive={isTimelineCardExpanded} {...card} />;
    case 'PlaceBidCard':
      return <BiddingAndPricingController dealId={dealId} {...card} />;
    case 'BidCard':
      return <BidCardBody dealId={dealId} {...card} />;
    case 'SettlementCard':
      return <SettlementCardBody dealId={dealId} seller={seller} {...card} />;
    case 'WireConfirmationCard':
      return <WireConfirmationCardBody dealId={dealId} dealState={dealState} {...card} />;
    case 'PlaceholderCard':
      return null;
    default:
      return <DefaultCardBody />;
  }
}

const formatLastUpdatedDate = (lastUpdatedDate: string) => {
  const lastUpdatedDateTime = DateTime.fromISO(lastUpdatedDate);
  return `${lastUpdatedDateTime.toRelativeCalendar()} @ ${
    lastUpdatedDateTime.diffNow().as('days') > -1
      ? lastUpdatedDateTime.toFormat('tt')
      : lastUpdatedDateTime.toFormat('F')
  }`;
};

type EventActionCardProps = {
  card: GetTimeline_deal_cards;
  seller: GetDealGeneralInfo_deal_listing_UserCompanyListing_seller;
  dealId: string;
  role: string;
  isDefaultExpanded: boolean;
  isExpanded: boolean;
  expand: () => void;
  dealState: DealStagesDeal_state;
  cardIndex: number;
};

const EventActionCard = ({
  card,
  dealId,
  isExpanded,
  expand,
  role,
  seller,
  dealState,
  cardIndex,
}: EventActionCardProps): JSX.Element => {
  const lastUpdatedDate = useMemo(() => {
    if (card.__typename !== 'PlaceholderCard' && card.last_updated_date != null) {
      return formatLastUpdatedDate(card.last_updated_date);
    } else {
      return ``;
    }
  }, [card.last_updated_date, card.__typename]);

  const isPlaceholder = card.__typename === 'PlaceholderCard';
  const toolTip = isPlaceholder ? card.tooltip : '';
  const toolTipId = `${card.title}-tooltip`;

  return (
    <>
      <Card
        active={isExpanded}
        disabled={isPlaceholder}
        data-tooltip-id={toolTipId}
        onClick={() => {
          if (card.__typename !== 'PlaceholderCard') expand();
        }}
      >
        <div className="flex justify-between">
          <h1 className="mb-0 text-[13px]">{card.title}</h1>
          <div>
            <span className="text-xs text-gray-700">{lastUpdatedDate}</span>
          </div>
        </div>
        <div
          className={`${
            isExpanded ? 'mt-2 max-h-full overflow-visible' : 'mt-0 max-h-0 overflow-hidden'
          }`}
        >
          {renderCardBody(card, dealId, role, seller, dealState, isExpanded, cardIndex)}
        </div>
      </Card>
      {isPlaceholder && (
        <Tooltip place="top" variant="light" id={toolTipId}>
          {toolTip}
        </Tooltip>
      )}
    </>
  );
};

type EventActionCardsProps = {
  dealId: string;
  role: string;
  cards: GetTimeline_deal_cards[];
  seller: GetDealGeneralInfo_deal_listing_UserCompanyListing_seller;
  dealState: DealStagesDeal_state;
};

const EventActionCards = memo(
  ({ dealId, cards, role, seller, dealState }: EventActionCardsProps): JSX.Element => {
    const isCardShown = useIsTimelineCardShown();
    const lastNonPlaceholderIndex = useMemo(
      () =>
        findLastIndex(cards, (card) => card.__typename !== 'PlaceholderCard' && isCardShown(card)),
      [cards, isCardShown],
    );
    const getCardKey = (card: GetTimeline_deal_cards, index: number): string =>
      card.title === 'Bidding and Pricing' ? card.title : `${card.title}-${index}`;

    const { currentCard, setCurrentCard, userSelectedCard, setUserSelectedCard } =
      useDealRoomContext();

    useEffect(() => {
      // Check for cards under review first, but only if user hasn't manually selected
      const underReviewCard = cards.find(
        (card) => 'is_under_review' in card && card.is_under_review,
      );

      if (underReviewCard && !userSelectedCard) {
        const underReviewIndex = cards.indexOf(underReviewCard);
        const underReviewCardKey = getCardKey(underReviewCard, underReviewIndex);
        setCurrentCard(underReviewCardKey);
      } else if (lastNonPlaceholderIndex >= 0 && !currentCard && !userSelectedCard) {
        const cardKey = getCardKey(cards[lastNonPlaceholderIndex], lastNonPlaceholderIndex);
        setCurrentCard(cardKey);
      }
    }, [lastNonPlaceholderIndex, cards, currentCard, setCurrentCard, userSelectedCard]);

    return (
      <div className="h-full flex-grow overflow-auto">
        <div className="flex h-max flex-1 flex-col">
          {cards.map((card, index) => {
            // Hidden cards still consume their server index: StatusTracker and
            // CarveDetailsCardBody derive card keys from that position.
            if (!isCardShown(card)) return null;
            const cardKey = getCardKey(card, index);
            return (
              <EventActionCard
                key={cardKey}
                seller={seller}
                role={role}
                card={card}
                dealId={dealId}
                cardIndex={index}
                expand={() => {
                  setUserSelectedCard(cardKey);
                  setCurrentCard(cardKey);
                }}
                isExpanded={cardKey === currentCard}
                isDefaultExpanded={index === lastNonPlaceholderIndex}
                dealState={dealState}
              />
            );
          })}
        </div>
      </div>
    );
  },
);

const DefaultCardBody = (): JSX.Element => {
  return <div />;
};

export default EventActionCards;
